import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaTruck, FaSave, FaEdit, FaTrash, FaPlus, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaBuilding, FaSpinner, FaShieldAlt, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import './ColissimoParams.css';

const getEmptyFormData = () => ({
  contract_number: '',
  password: '',
  return_company_name: '',
  return_last_name: '',
  return_first_name: '',
  return_line1: '',
  return_line2: '',
  return_city: '',
  return_zip_code: '',
  return_country_code: 'FR',
  return_phone_number: '',
  return_email: ''
});

const ColissimoParams = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [formData, setFormData] = useState(getEmptyFormData);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/colissimo_settings/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setFormData(response.data);
      setIsCreating(false);
    } catch (error) {
      if (error.response?.status === 404) {
        // No settings found, user needs to create them
        setSettings(null);
        setIsCreating(true);
      } else {
        console.error('Failed to fetch Colissimo settings:', error);
        setPopupMessage(t('colissimoParams.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      if (isCreating) {
        // Create new settings
        const response = await axios.post('/api/colissimo_settings/settings', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(response.data);
        setIsCreating(false);
        setPopupMessage(t('colissimoParams.settingsCreatedSuccess'));
      } else {
        // Update existing settings
        const response = await axios.put('/api/colissimo_settings/settings', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(response.data);
        setIsEditing(false);
        setPopupMessage(t('colissimoParams.settingsUpdatedSuccess'));
      }
    } catch (error) {
      console.error('Failed to save Colissimo settings:', error);
      const errorMessage = error.response?.data?.detail || t('colissimoParams.failedToSave');
      setPopupMessage(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('colissimoParams.confirmDelete'))) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/colissimo_settings/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(null);
      setIsCreating(true);
      setIsEditing(false);
      setFormData(getEmptyFormData());
      setPopupMessage(t('colissimoParams.settingsDeletedSuccess'));
    } catch (error) {
      console.error('Failed to delete Colissimo settings:', error);
      setPopupMessage(t('colissimoParams.failedToDelete'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (settings) {
      setFormData(settings);
      setIsEditing(false);
    } else {
      setFormData(getEmptyFormData());
      setIsCreating(false);
    }
  };

  const isFormEnabled = isEditing || isCreating;

  if (loading) {
    return (
      <div className="colissimo-params-page">
        <div className="colissimo-loading-card">
          <FaSpinner className="colissimo-spinner" />
          <p>{t('colissimoParams.loading', 'Loading Colissimo settings...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="colissimo-params-page">
      <div className="colissimo-hero">
        <div className="colissimo-hero-title">
          <span className="colissimo-hero-icon">
            <FaTruck />
          </span>
          <div>
            <p className="colissimo-eyebrow">{t('parameters', 'Parameters')}</p>
            <h1>{t('colissimoParams.title', 'Colissimo Settings')}</h1>
            <p>{t('colissimoParams.subtitle', 'Configure your label credentials and return sender details for Colissimo shipments.')}</p>
          </div>
        </div>

        <div className={`colissimo-status-card ${settings ? 'configured' : 'missing'}`}>
          {settings ? <FaCheckCircle /> : <FaInfoCircle />}
          <div>
            <span>{settings ? t('colissimoParams.configured', 'Configured') : t('colissimoParams.notConfigured', 'Not configured')}</span>
            <strong>{settings ? t('colissimoParams.readyToUse', 'Ready to create labels') : t('colissimoParams.setupRequired', 'Setup required')}</strong>
          </div>
        </div>
      </div>

      {popupMessage && (
        <div className="colissimo-feedback">
          <span>{popupMessage}</span>
          <button type="button" onClick={() => setPopupMessage('')}>×</button>
        </div>
      )}

      {!settings && !isCreating ? (
        <div className="colissimo-empty-card">
          <span className="colissimo-empty-icon">
            <FaTruck />
          </span>
          <h2>{t('colissimoParams.noSettingsConfigured', 'No Colissimo settings configured.')}</h2>
          <p>{t('colissimoParams.emptyDescription', 'Add your contract details once to enable Colissimo label generation from the workspace.')}</p>
          <button 
            type="button"
            className="colissimo-primary-button"
            onClick={() => setIsCreating(true)}
          >
            <FaPlus /> {t('colissimoParams.createSettings', 'Create Colissimo Settings')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="colissimo-form">
          <section className="colissimo-panel">
            <div className="colissimo-panel-header">
              <div>
                <h2>
                  <FaShieldAlt /> {t('colissimoParams.apiCredentials', 'API Credentials')}
                </h2>
                <p>{t('colissimoParams.apiCredentialsDescription', 'Store the account used to authenticate Colissimo shipping requests.')}</p>
              </div>
            </div>
            <div className="colissimo-grid two-columns">
              <div className="colissimo-field">
                <label htmlFor="contract_number">
                  <FaBuilding /> {t('colissimoParams.contractNumber', 'Contract Number *')}
                </label>
                <input
                  type="text"
                  id="contract_number"
                  name="contract_number"
                  value={formData.contract_number}
                  onChange={handleInputChange}
                  required
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="password">
                  <FaEye /> {t('colissimoParams.password', 'Password *')}
                </label>
                <div className="colissimo-password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={!isFormEnabled}
                  />
                  <button
                    type="button"
                    className="colissimo-icon-button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('colissimoParams.hidePassword', 'Hide password') : t('colissimoParams.showPassword', 'Show password')}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="colissimo-panel">
            <div className="colissimo-panel-header">
              <div>
                <h2>
                  <FaBuilding /> {t('colissimoParams.returnAddressInformation', 'Return Address Information')}
                </h2>
                <p>{t('colissimoParams.returnAddressDescription', 'These details appear as the sender and return address on labels.')}</p>
              </div>
            </div>
            <div className="colissimo-grid three-columns">
              <div className="colissimo-field">
                <label htmlFor="return_company_name">
                  <FaBuilding /> {t('colissimoParams.companyName', 'Company Name')}
                </label>
                <input
                  type="text"
                  id="return_company_name"
                  name="return_company_name"
                  value={formData.return_company_name}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_first_name">
                  <FaUser /> {t('colissimoParams.firstName', 'First Name')}
                </label>
                <input
                  type="text"
                  id="return_first_name"
                  name="return_first_name"
                  value={formData.return_first_name}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_last_name">
                  <FaUser /> {t('colissimoParams.lastName', 'Last Name')}
                </label>
                <input
                  type="text"
                  id="return_last_name"
                  name="return_last_name"
                  value={formData.return_last_name}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
            </div>
          </section>

          <section className="colissimo-panel">
            <div className="colissimo-panel-header">
              <div>
                <h2>
                  <FaMapMarkerAlt /> {t('colissimoParams.returnAddress', 'Return Address')}
                </h2>
                <p>{t('colissimoParams.returnAddressFieldsDescription', 'Keep the address aligned with your Colissimo contract details.')}</p>
              </div>
            </div>
            <div className="colissimo-grid">
              <div className="colissimo-field full-width">
                <label htmlFor="return_line1">
                  <FaMapMarkerAlt /> {t('colissimoParams.addressLine1', 'Address Line 1')}
                </label>
                <input
                  type="text"
                  id="return_line1"
                  name="return_line1"
                  value={formData.return_line1}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field full-width">
                <label htmlFor="return_line2">
                  <FaMapMarkerAlt /> {t('colissimoParams.addressLine2', 'Address Line 2')}
                </label>
                <input
                  type="text"
                  id="return_line2"
                  name="return_line2"
                  value={formData.return_line2}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_city">
                  <FaMapMarkerAlt /> {t('colissimoParams.city', 'City')}
                </label>
                <input
                  type="text"
                  id="return_city"
                  name="return_city"
                  value={formData.return_city}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_zip_code">
                  <FaMapMarkerAlt /> {t('colissimoParams.zipCode', 'Zip Code')}
                </label>
                <input
                  type="text"
                  id="return_zip_code"
                  name="return_zip_code"
                  value={formData.return_zip_code}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_country_code">
                  <FaMapMarkerAlt /> {t('colissimoParams.countryCode', 'Country Code')}
                </label>
                <select
                  id="return_country_code"
                  name="return_country_code"
                  value={formData.return_country_code}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                >
                  <option value="FR">France (FR)</option>
                  <option value="BE">Belgium (BE)</option>
                  <option value="DE">Germany (DE)</option>
                  <option value="ES">Spain (ES)</option>
                  <option value="IT">Italy (IT)</option>
                  <option value="CH">Switzerland (CH)</option>
                  <option value="LU">Luxembourg (LU)</option>
                </select>
              </div>
            </div>
          </section>

          <section className="colissimo-panel">
            <div className="colissimo-panel-header">
              <div>
                <h2>
                  <FaEnvelope /> {t('colissimoParams.returnContactInformation', 'Return Contact Information')}
                </h2>
                <p>{t('colissimoParams.returnContactDescription', 'Contact data used when carriers need sender information for a return.')}</p>
              </div>
            </div>
            <div className="colissimo-grid two-columns">
              <div className="colissimo-field">
                <label htmlFor="return_phone_number">
                  <FaPhone /> {t('colissimoParams.phoneNumber', 'Phone Number')}
                </label>
                <input
                  type="tel"
                  id="return_phone_number"
                  name="return_phone_number"
                  value={formData.return_phone_number}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
              <div className="colissimo-field">
                <label htmlFor="return_email">
                  <FaEnvelope /> {t('colissimoParams.email', 'Email')}
                </label>
                <input
                  type="email"
                  id="return_email"
                  name="return_email"
                  value={formData.return_email}
                  onChange={handleInputChange}
                  disabled={!isFormEnabled}
                />
              </div>
            </div>
          </section>

          <div className="colissimo-actions">
            {isCreating || isEditing ? (
              <>
                <button 
                  type="submit" 
                  className="colissimo-primary-button"
                  disabled={saving}
                >
                  {saving ? <FaSpinner className="colissimo-spinner inline" /> : <FaSave />}
                  {saving ? t('colissimoParams.saving', 'Saving...') : (isCreating ? t('colissimoParams.createSettings', 'Create Colissimo Settings') : t('colissimoParams.saveChanges', 'Save Changes'))}
                </button>
                <button 
                  type="button" 
                  className="colissimo-secondary-button"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  {t('colissimoParams.cancel', 'Cancel')}
                </button>
              </>
            ) : settings ? (
              <>
                <button 
                  type="button" 
                  className="colissimo-primary-button"
                  onClick={() => setIsEditing(true)}
                >
                  <FaEdit /> {t('colissimoParams.editSettings', 'Edit Settings')}
                </button>
                <button 
                  type="button" 
                  className="colissimo-danger-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <FaTrash /> {saving ? t('colissimoParams.deleting', 'Deleting...') : t('colissimoParams.deleteSettings', 'Delete Settings')}
                </button>
              </>
            ) : null}
          </div>
        </form>
      )}
    </div>
  );
};

export default ColissimoParams;